import { useState } from 'react'
import type { Schemas } from '@npc/api-client'
import { Link } from '@tanstack/react-router'

import { monthDayTime } from '../../../app/format'
import { isForbiddenError } from '../../../app/errors'
import {
  useAssignProjectHost,
  useCompleteHostOnboarding,
  useProject,
  useProjectResources,
  useUnassignProjectHost,
} from '../../../app/queries'
import { Modal } from '../../../components/modal'
import { T } from '../../../copy/text'
import { t } from '../../../copy'
import { PJ } from '../../../copy/pjshell-text'
import { useProjectId } from './params'
import {
  DataCell,
  DataRow,
  DataTable,
  EmptyState,
  ModuleError,
  ModuleHeader,
  ModuleNotice,
  ModulePage,
  ModulePanel,
  ModuleSkeleton,
  Uncollected,
  Unconfigured,
  luluButtonClass,
  luluDangerButtonClass,
  luluPrimaryButtonClass,
} from './ui'

export function ProjectResourcesPage() {
  const projectId = useProjectId()
  if (!projectId) {
    return (
      <ModulePage testid="project-module-resources">
        <ModuleSkeleton />
      </ModulePage>
    )
  }
  return <Loaded projectId={projectId} />
}

export { ProjectResourcesPage as ProjectResources }

function Loaded({ projectId }: { projectId: string }) {
  const project = useProject(projectId)
  const resources = useProjectResources(projectId)
  const assign = useAssignProjectHost(projectId)
  const unassign = useUnassignProjectHost(projectId)
  const [pendingHost, setPendingHost] = useState<Schemas['ProjectResourceHost'] | null>(null)

  if (resources.isPending) {
    return (
      <ModulePage testid="project-module-resources">
        <ModuleSkeleton />
      </ModulePage>
    )
  }
  if (resources.error) {
    return (
      <ModulePage testid="project-module-resources">
        <ModuleHeader
          title={<PJ id="pjshell.resource.title" />}
          subtitle={<PJ id="pjshell.resource.subtitle" />}
        />
        {isForbiddenError(resources.error) ? (
          <ModuleError message={t('error.forbidden.hosts')} />
        ) : (
          <div
            className="rounded-npc-chip border border-npc-lulu-line-2 bg-npc-lulu-bg-1 px-npc-grid-sm py-npc-gap-sm"
            data-testid="pjshell-resources-read-failed"
          >
            <div className="text-npc-body-sm font-medium text-npc-lulu-t1">
              <T id="error.read_failed.title" />
            </div>
            <div className="mt-npc-gap-xs text-npc-caption text-npc-lulu-t3">
              <T id="error.generic" />
            </div>
            <button type="button" className={`${luluButtonClass} mt-npc-gap-sm`} onClick={() => void resources.refetch()}>
              <T id="error.read_failed.retry" />
            </button>
          </div>
        )}
      </ModulePage>
    )
  }
  if (!resources.data) {
    return (
      <ModulePage testid="project-module-resources">
        <ModuleSkeleton />
      </ModulePage>
    )
  }

  const items = resources.data.hosts.filter(
    (host) => host.occupancy === 'exclusive' || host.occupancy === 'shared',
  )
  const pool = resources.data.pool.filter((host) => host.occupancy === 'unassigned')
  const showVendor = items.some((host) => Boolean(host.vendor_name?.trim()))
  const hostGrid = showVendor
    ? 'minmax(0,1.2fr) 82px 96px minmax(0,1.1fr) minmax(0,1fr) 96px minmax(0,0.8fr) 88px'
    : 'minmax(0,1.2fr) 82px 96px minmax(0,1.1fr) minmax(0,1fr) 96px 88px'
  const projectName = project.data?.name?.trim() || t('nav.project.loading')
  const lastProd = pendingHost ? isLastExclusiveProduction(pendingHost, items) : false

  return (
    <ModulePage testid="project-module-resources">
      <ModuleHeader
        title={<PJ id="pjshell.resource.title" />}
        subtitle={<PJ id="pjshell.resource.subtitle" />}
      />

      {items.length === 0 ? null : (
        <ModuleNotice
          tone="neutral"
          title={<T id="dashboard.project.host_count" vars={{ count: resources.data.assigned_count }} />}
          stats={[
            {
              value: countOccupancy(items, 'exclusive'),
              label: <PJ id="pjshell.resource.exclusive" />,
            },
            {
              value: countOccupancy(items, 'shared'),
              label: <PJ id="pjshell.resource.shared" />,
            },
            { value: pool.length, label: <PJ id="pjshell.resource.pool" /> },
            {
              value: resources.data.earliest_expiry_at ? (
                monthDayTime(resources.data.earliest_expiry_at)
              ) : (
                <Uncollected />
              ),
              label: <PJ id="pjshell.resource.col.expiry" />,
            },
          ]}
        />
      )}

      {items.length === 0 ? (
        <EmptyState
          testid="pjshell-hosts-empty"
          title={<PJ id="pjshell.resource.empty.title" />}
          body={<PJ id="pjshell.resource.empty.body" />}
          action={
            <div className="flex flex-col gap-npc-gap-sm sm:flex-row">
              <a href="#pjshell-assign-idle" className={luluPrimaryButtonClass}>
                <PJ id="pjshell.resource.assign_idle" />
              </a>
              <Link to="/hosts/new" search={{ project_id: projectId }} className={luluButtonClass}>
                <PJ id="pjshell.resource.add_one" />
              </Link>
            </div>
          }
        />
      ) : (
        <div data-testid="pjshell-hosts" className="overflow-x-auto">
          <div className="min-w-npc-main-min">
          <DataTable
            grid={hostGrid}
            hint={<T id="project.section.where_it_lives" />}
            columns={[
              <PJ key="host" id="pjshell.resource.col.host" />,
              <PJ key="owner" id="pjshell.resource.col.owner" />,
              <PJ key="env" id="pjshell.resource.col.env" />,
              <PJ key="spec" id="pjshell.resource.col.spec" />,
              <PJ key="cap" id="pjshell.resource.col.capacity" />,
              <PJ key="exp" id="pjshell.resource.col.expiry" />,
              ...(showVendor ? [<PJ key="vendor" id="pjshell.resource.col.vendor" />] : []),
              <PJ key="act" id="pjshell.resource.col.actions" />,
            ]}
          >
            {items.map((host) => (
              <div key={host.id} className="relative">
                <Link
                  to="/hosts/$hostId"
                  params={{ hostId: host.id }}
                  aria-label={host.name}
                  className="absolute inset-0 z-0"
                />
                <DataRow grid={hostGrid} warn={host.status === 'offline' || host.status === 'error'}>
                  <DataCell>
                    <div className="relative z-[1] flex min-w-0 flex-col gap-npc-gap-xs">
                      <span>{host.name}</span>
                      {host.address ? (
                        <span className="font-npc-mono text-npc-meta text-npc-lulu-t3">{host.address}</span>
                      ) : null}
                      <HostReadiness host={host} projectId={projectId} />
                      {host.probe_status ? (
                        <span className="text-npc-meta text-npc-lulu-t3">{host.probe_status}</span>
                      ) : null}
                    </div>
                  </DataCell>
                  <DataCell>
                    <div className="relative z-[1] flex min-w-0 flex-col gap-npc-gap-xs">
                      {occupancyLabel(host.occupancy)}
                      {host.projects.filter((item) => item.name.trim() !== '').length > 0 ? (
                        <span className="text-npc-meta text-npc-lulu-t3">
                          {host.projects
                            .map((item) => item.name.trim())
                            .filter((name) => name !== '')
                            .join(' · ')}
                        </span>
                      ) : null}
                    </div>
                  </DataCell>
                  <DataCell>
                    {host.environment_kind === 'production' ? (
                      <PJ id="pjshell.env.production" />
                    ) : host.environment_kind === 'test' ? (
                      <PJ id="pjshell.env.test" />
                    ) : (
                      <Uncollected />
                    )}
                  </DataCell>
                  <DataCell mono>{host.spec_text ? host.spec_text : <Uncollected />}</DataCell>
                  <DataCell>
                    <CapacityCell host={host} />
                  </DataCell>
                  <DataCell>
                    {host.expires_at ? monthDayTime(host.expires_at) : <Uncollected />}
                  </DataCell>
                  {showVendor ? (
                    <DataCell>{host.vendor_name?.trim() ? host.vendor_name : <Uncollected />}</DataCell>
                  ) : null}
                  <DataCell>
                    <button
                      type="button"
                      data-testid={`pjshell-unassign-${host.id}`}
                      className={`${luluButtonClass} relative z-10 min-h-11`}
                      onClick={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                        setPendingHost(host)
                      }}
                    >
                      <PJ id="pjshell.resource.unassign" />
                    </button>
                  </DataCell>
                </DataRow>
              </div>
            ))}
          </DataTable>
          </div>
        </div>
      )}

      <div className="mt-npc-gap-sm" id="pjshell-assign-idle">
        <AssignIdleHosts
          projectId={projectId}
          hosts={pool}
          pending={resources.isFetching && pool.length === 0}
          assigningHostId={assign.isPending ? assign.variables : undefined}
          assignError={assign.error?.message}
          onAssign={(hostId) => assign.mutate(hostId)}
        />
      </div>

      <div className="mt-npc-gap-sm">
        <DomainEntryTable projectId={projectId} resources={resources.data} />
      </div>

      <Modal
        open={pendingHost !== null}
        onOpenChange={(open) => {
          if (!open && !unassign.isPending) setPendingHost(null)
        }}
        title={
          pendingHost ? (
            <span className={lastProd ? 'text-npc-lulu-bad' : undefined}>
              <PJ id="pjshell.resource.unassign.title" vars={{ project_name: projectName }} />
            </span>
          ) : (
            ''
          )
        }
        description={
          lastProd ? (
            <PJ id="pjshell.resource.unassign.last_prod" />
          ) : (
            <PJ id="pjshell.resource.unassign.body" />
          )
        }
        className="border border-npc-lulu-line-3 bg-npc-lulu-bg-1 text-npc-lulu-t1"
        footer={
          <>
            <button
              type="button"
              className={luluButtonClass}
              disabled={unassign.isPending}
              onClick={() => setPendingHost(null)}
            >
              <T id="ui.cancel" />
            </button>
            <button
              type="button"
              data-testid="pjshell-unassign-confirm"
              className={lastProd ? luluDangerButtonClass : luluPrimaryButtonClass}
              disabled={unassign.isPending || !pendingHost}
              onClick={() => {
                if (!pendingHost) return
                unassign.mutate(pendingHost.id, {
                  onSuccess: () => setPendingHost(null),
                })
              }}
            >
              <PJ id="pjshell.resource.unassign.confirm" />
            </button>
          </>
        }
      >
        {unassign.error ? <ModuleError message={t('error.generic')} /> : null}
      </Modal>
    </ModulePage>
  )
}

function HostReadiness({ host, projectId }: { host: Schemas['ProjectResourceHost']; projectId: string }) {
  const complete = useCompleteHostOnboarding(projectId)
  const check = host.readiness
  const failures = {
    ssh_unavailable: 'pjshell.readiness.ssh_unavailable',
    installer_unavailable: 'pjshell.readiness.installer_unavailable',
    install_failed: 'pjshell.readiness.install_failed',
    install_timeout: 'pjshell.readiness.install_timeout',
    check_failed: 'pjshell.readiness.check_failed',
    awaiting_heartbeat: 'pjshell.readiness.awaiting_heartbeat',
    execution_failed: 'pjshell.readiness.execution_failed',
    agent_only: 'pjshell.readiness.agent_only',
  } as const
  const failure = failures[check?.failure_code as keyof typeof failures]
  return <div className="relative z-10 flex flex-col items-start gap-npc-gap-xs" data-testid={`host-readiness-${host.id}`}>
    <span className={check?.deployable ? 'text-npc-lulu-t1' : 'text-npc-lulu-warn'}>
      <PJ id={check?.deployable ? 'pjshell.readiness.ready' : 'pjshell.readiness.pending'} />
    </span>
    <details className="text-npc-meta text-npc-lulu-t3">
      <summary className="cursor-pointer"><PJ id="pjshell.readiness.details" /></summary>
      <ol className="my-npc-gap-xs list-none p-0">
        {(['registered', 'heartbeat', 'execution'] as const).map(step => <li key={step}>
          <span aria-hidden="true">{check?.[step] ? '✓ ' : '○ '}</span><span className="sr-only"><PJ id={check?.[step] ? 'pjshell.readiness.complete' : 'pjshell.readiness.waiting'} /></span><PJ id={`pjshell.readiness.${step}`} />
        </li>)}
      </ol>
      {failure ? <p role="status"><PJ id={failure} /></p> : null}
      <p><PJ id="pjshell.readiness.scope" /></p>
      <p><PJ id="pjshell.readiness.validity" /></p>
      <Link to="/hosts/$hostId" params={{ hostId: host.id }} className="text-npc-lulu-t3 underline hover:text-npc-lulu-t1"><PJ id="pjshell.readiness.details" /></Link>
    </details>
    {complete.error ? <span role="alert"><T id="error.generic" /></span> : null}
    <button type="button" className={luluButtonClass} disabled={complete.isPending || check?.running}
      onClick={() => complete.mutate(host.id)}>
      <PJ id={complete.isPending || check?.running ? 'pjshell.readiness.running' : check?.deployable ? 'pjshell.readiness.verify' : 'pjshell.readiness.continue'} />
    </button>
  </div>
}

function AssignIdleHosts({
  projectId,
  hosts,
  pending,
  assigningHostId,
  assignError,
  onAssign,
}: {
  projectId: string
  hosts: Schemas['ProjectResourceHost'][]
  pending: boolean
  assigningHostId?: string
  assignError?: string
  onAssign: (hostId: string) => void
}) {
  return (
    <ModulePanel title={<T id="project.action.assign_host" />} testid="pjshell-assign-idle">
      <div className="mb-npc-gap-sm flex flex-wrap items-center justify-between gap-npc-gap-sm">
        <div className="text-npc-meta text-npc-lulu-t3">
          <T id="project.assign.idle_heading" />
        </div>
        <Link
          to="/hosts/new"
          search={{ project_id: projectId }}
          className="text-npc-caption text-npc-lulu-t3 hover:text-npc-lulu-t1"
        >
          <T id="project.action.add_new_host" />
        </Link>
      </div>
      {pending ? (
        <T id="project.assign.idle_loading" />
      ) : hosts.length === 0 ? (
        <T id="project.assign.idle_empty" />
      ) : (
        <ul className="m-0 flex list-none flex-col gap-npc-gap-xs p-0">
          {hosts.map((host) => (
            <li key={host.id} className="flex flex-wrap items-center justify-between gap-npc-gap-sm">
              <div className="min-w-0">
                <div>{host.name}</div>
                {host.address ? (
                  <div className="font-npc-mono text-npc-meta text-npc-lulu-t3">{host.address}</div>
                ) : null}
              </div>
              <button
                type="button"
                data-testid={`pjshell-assign-${host.id}`}
                className={luluButtonClass}
                disabled={assigningHostId === host.id}
                onClick={() => onAssign(host.id)}
              >
                <PJ id="pjshell.resource.assign_to_project" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {assignError ? <ModuleError message={assignError} /> : null}
    </ModulePanel>
  )
}

function DomainEntryTable({
  projectId,
  resources,
}: {
  projectId: string
  resources: Schemas['ProjectResources']
}) {
  const label = resources.dns_name?.trim()
  const configured = Boolean(label) && resources.dns_status === 'configured'
  const vendor = resources.hosts.map((host) => host.vendor_name?.trim()).find((name) => Boolean(name))
  const showVendor = Boolean(vendor)
  const entryGrid = showVendor
    ? 'minmax(0,1.3fr) 96px minmax(0,1.2fr) 108px 108px'
    : 'minmax(0,1.3fr) 96px minmax(0,1.2fr) 108px'

  return (
    <DataTable
      grid={entryGrid}
      hint={<PJ id="pjshell.resource.domains" />}
      columns={[
        <PJ key="e" id="pjshell.resource.col.entry" />,
        <PJ key="t" id="pjshell.resource.col.type" />,
        <PJ key="p" id="pjshell.resource.col.point" />,
        ...(showVendor ? [<PJ key="v" id="pjshell.resource.col.vendor" />] : []),
        <PJ key="c" id="pjshell.resource.col.cert" />,
      ]}
    >
      <DataRow grid={entryGrid}>
        <DataCell mono={configured}>
          {configured && label ? (
            <ConfiguredEntry label={label} />
          ) : (
            <Unconfigured />
          )}
        </DataCell>
        <DataCell>
          {resources.dns_status === 'configured' ? (
            <span data-testid="pjshell-dns-configured">DNS</span>
          ) : (
            <DnsUnconfigured projectId={projectId} />
          )}
        </DataCell>
        <DataCell mono>
          {configured && label ? label : <Unconfigured testid="pjshell-dns-point-unconfigured" />}
        </DataCell>
        {showVendor ? <DataCell>{vendor}</DataCell> : null}
        <DataCell>
          {resources.cert_status === 'configured' ? (
            <span data-testid="pjshell-cert-configured">
              {resources.cert_expires_at ? monthDayTime(resources.cert_expires_at) : <Uncollected />}
            </span>
          ) : (
            <CertUnconfigured projectId={projectId} />
          )}
        </DataCell>
      </DataRow>
    </DataTable>
  )
}

function DnsUnconfigured({ projectId }: { projectId: string }) {
  return (
    <div data-testid="pjshell-dns-unconfigured" className="flex min-w-0 flex-col gap-npc-gap-xs">
      <Unconfigured />
      <Link
        to="/projects/$projectId/environments"
        params={{ projectId }}
        className="text-npc-caption text-npc-lulu-t3 hover:text-npc-lulu-t1"
      >
        <PJ id="pjshell.resource.go_dns" />
      </Link>
    </div>
  )
}

function CertUnconfigured({ projectId }: { projectId: string }) {
  return (
    <div data-testid="pjshell-cert-unconfigured" className="flex min-w-0 flex-col gap-npc-gap-xs">
      <Unconfigured />
      <Link
        to="/projects/$projectId/releases"
        params={{ projectId }}
        className="text-npc-caption text-npc-lulu-t3 hover:text-npc-lulu-t1"
      >
        <PJ id="pjshell.resource.go_cert" />
      </Link>
    </div>
  )
}

function occupancyLabel(value: Schemas['ProjectResourceHost']['occupancy']) {
  if (value === 'exclusive') return <PJ id="pjshell.resource.exclusive" />
  if (value === 'shared') return <PJ id="pjshell.resource.shared" />
  return <Uncollected />
}

function countOccupancy(hosts: Schemas['ProjectResourceHost'][], want: 'exclusive' | 'shared'): number {
  return hosts.filter((host) => host.occupancy === want).length
}

function isLastExclusiveProduction(
  host: Schemas['ProjectResourceHost'],
  hosts: Schemas['ProjectResourceHost'][],
): boolean {
  if (host.occupancy !== 'exclusive') return false
  if (host.environment_kind !== 'production') return false
  return !hosts.some(
    (other) =>
      other.id !== host.id &&
      other.occupancy === 'exclusive' &&
      other.environment_kind === 'production',
  )
}

function CapacityCell({ host }: { host: Schemas['ProjectResourceHost'] }) {
  const kind = diskCapacityKind(host)
  const ratio = host.disk_used_ratio
  return (
    <span data-testid={`pjshell-capacity-${host.id}`}>
      {kind === 'failed' ? (
        <PJ id="pjshell.fact.probe_failed" />
      ) : kind === 'live' && ratio != null ? (
        <T id={diskCapacityId(ratio)} />
      ) : (
        <Uncollected />
      )}
    </span>
  )
}

/**
 * 有当场有效的用量才给人话。离线、出错、过期一律写采集失败，
 * 从没采过写未采集。不许用「未知」把失败盖过去。
 */
export function diskCapacityKind(host: {
  status: string
  disk_used_ratio?: number | null
  probe_at?: string | null
  stale?: boolean
  unknown?: boolean
}): 'live' | 'failed' | 'uncollected' {
  const probeDown = host.status === 'offline' || host.status === 'error'
  const metricsUnusable = host.stale === true || host.unknown === true
  if (probeDown || metricsUnusable) return 'failed'
  if (host.disk_used_ratio != null) return 'live'
  if (host.probe_at) return 'failed'
  return 'uncollected'
}

export function diskCapacityId(
  ratio: number,
): 'host.metric.disk.ok' | 'host.metric.disk.filling' | 'host.metric.disk.full' {
  const pct = ratio <= 1 ? ratio * 100 : ratio
  if (pct >= 90) return 'host.metric.disk.full'
  if (pct >= 75) return 'host.metric.disk.filling'
  return 'host.metric.disk.ok'
}

function ConfiguredEntry({ label }: { label: string }) {
  const href = receiptOpenHref(label)
  if (!href) return <>{label}</>
  return (
    <a href={href} target="_blank" rel="noreferrer" className="font-npc-mono text-npc-lulu-t1 hover:underline">
      {label}
    </a>
  )
}

/** 只开回执里已经是 URL 的地址，不发明 https://。 */
export function receiptOpenHref(value: string | null | undefined): string | null {
  const raw = value?.trim() ?? ''
  if (raw.startsWith('https://') || raw.startsWith('http://')) return raw
  return null
}
